import React, { useEffect, useState } from "react";
import { Zap, Loader2, RefreshCw, Gauge, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const MODES = [
  ["auto", "Auto", "Uses NVENC when a working NVIDIA GPU is detected, otherwise CPU"],
  ["nvenc", "Prefer NVENC", "Always try hardware first; falls back to CPU with a warning"],
  ["cpu", "CPU only", "libx264 / libx265 software encoding"],
];

const Field = ({ label, children }) => (<div><label className="metric-label block mb-1">{label}</label>{children}</div>);

export const GpuProfile = () => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState("");
  useEffect(() => { api.gpu().then(setD).catch(() => {}); }, []);
  if (!d) return <div className="studio-card p-5"><Loader2 className="animate-spin text-[#E6B04E]" /></div>;

  const save = async (patch) => { try { setD(await api.setEncoderProfile(patch)); toast.success("Encoder profile saved"); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const run = async (k, fn) => { setBusy(k); try { setD(await fn()); } catch { toast.error("Failed"); } finally { setBusy(""); } };
  const p = d.profile;

  return (
    <div className="studio-card p-5 space-y-5" data-testid="gpu-profile">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2"><Zap size={16} className="text-[#E6B04E]" /><span className="font-head font-bold">GPU Deploy Profile</span></div>
        <div className="flex gap-2">
          <button className="btn btn-ghost !py-1.5 text-xs" onClick={() => run("detect", () => api.gpu(true))} disabled={!!busy} data-testid="btn-gpu-detect">
            {busy === "detect" ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}Re-detect</button>
          <button className="btn btn-gold !py-1.5 text-xs" onClick={() => run("bench", api.benchmark)} disabled={!!busy} data-testid="btn-gpu-benchmark">
            {busy === "bench" ? <Loader2 size={13} className="animate-spin" /> : <Gauge size={13} />}{busy === "bench" ? "Benchmarking…" : "Run encode benchmark"}</button>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <div><div className="metric-label">GPU</div><div className="font-semibold" data-testid="gpu-name">{d.gpus[0] ? `${d.gpus[0].name} · ${d.gpus[0].vram_mb}MB` : "None detected"}</div></div>
        <div><div className="metric-label">NVENC H.264</div><div data-testid="gpu-nvenc-h264" className={d.nvenc_h264 ? "text-[#34d399]" : "text-[#94A3B8]"}>{d.nvenc_h264 ? "usable" : "unavailable"}</div></div>
        <div><div className="metric-label">NVENC HEVC</div><div data-testid="gpu-nvenc-hevc" className={d.nvenc_hevc ? "text-[#34d399]" : "text-[#94A3B8]"}>{d.nvenc_hevc ? "usable" : "unavailable"}</div></div>
        <div><div className="metric-label">Active encoder</div><div className="font-mono text-[#E6B04E]" data-testid="gpu-active-encoder">{d.active_encoder}</div></div>
      </div>
      {d.warning && <div className="flex items-start gap-2 text-xs text-[#fbbf24]" data-testid="gpu-warning"><AlertTriangle size={14} />{d.warning}</div>}
      <div className="grid md:grid-cols-3 gap-2">
        {MODES.map(([v, l, desc]) => (
          <button key={v} onClick={() => save({ mode: v })} data-testid={`gpu-mode-${v}`}
            className={`studio-card p-3 text-left transition-colors ${p.mode === v ? "border-[#E6B04E]" : "hover:border-[#3B4259]"}`}>
            <div className="font-semibold text-sm">{l}</div><div className="metric-label" style={{ fontSize: 9 }}>{desc}</div>
          </button>
        ))}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Field label="NVENC preset"><select className="studio-input !py-1.5 text-sm" value={p.nvenc_preset} onChange={(e) => save({ nvenc_preset: e.target.value })} data-testid="gpu-nvenc-preset">
          {d.nvenc_presets.map((x) => <option key={x} value={x}>{`${x}${x === "p1" ? " fastest" : x === "p7" ? " best" : ""}`}</option>)}</select></Field>
        <Field label="NVENC CQ"><input type="number" min="14" max="36" className="studio-input !py-1.5 text-sm" defaultValue={p.nvenc_cq} onBlur={(e) => save({ nvenc_cq: +e.target.value })} data-testid="gpu-nvenc-cq" /></Field>
        <Field label="CPU preset"><select className="studio-input !py-1.5 text-sm" value={p.cpu_preset} onChange={(e) => save({ cpu_preset: e.target.value })} data-testid="gpu-cpu-preset">
          {d.cpu_presets.map((x) => <option key={x} value={x}>{x}</option>)}</select></Field>
        <Field label="CPU CRF"><input type="number" min="14" max="36" className="studio-input !py-1.5 text-sm" defaultValue={p.cpu_crf} onBlur={(e) => save({ cpu_crf: +e.target.value })} data-testid="gpu-cpu-crf" /></Field>
      </div>
      {d.benchmark && (
        <div data-testid="gpu-benchmark-results">
          <div className="metric-label mb-2">Benchmark · 1080p30 · {new Date(d.benchmark.at).toLocaleString()}</div>
          {d.benchmark.rows.map((r) => (
            <div key={r.encoder} className="flex items-center gap-3 text-xs mb-1.5">
              <span className="font-mono w-24">{r.encoder}</span>
              <div className="flex-1 h-2 rounded-full bg-[#0c0e14] overflow-hidden"><div className="h-full bg-[#E6B04E]" style={{ width: `${Math.min(100, r.realtime_x * 25)}%` }} /></div>
              <span className="font-mono w-36 text-right">{r.ok ? `${r.fps} fps · ${r.realtime_x}× realtime` : "failed"}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
